import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { useSiteContent } from '../context/SiteContentContext';
import {
  Phone,
  Target,
  Eye,
  Heart,
  Users,
  MapPin,
  Award,
  Zap,
  Headphones,
  CheckCircle2,
  Play
} from 'lucide-react';

const iconMap = {
  Zap: Zap,
  Award: Award,
  HeadphonesIcon: Headphones,
  Target: Target,
  Eye: Eye,
  Heart: Heart,
  Users: Users,
  MapPin: MapPin
};

const QuemSomosPage = () => {
  const { siteContent } = useSiteContent();
  const companyInfo = siteContent.companyInfo;
  const images = siteContent.images;
  const differentials = siteContent.differentials;
  const aboutPage = siteContent.aboutPage || {};
  const aboutVideo = aboutPage.video || {};
  const hero = aboutPage.hero || {};
  const intro = aboutPage.intro || {};
  const videoSection = aboutPage.video || {};
  const pillars = aboutPage.pillars || {};
  const timeline = aboutPage.timeline || {};
  const differentialsSection = aboutPage.differentialsSection || {};
  const cta = aboutPage.cta || {};
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const videoEmbed = useMemo(() => {
    const url = (aboutVideo.videoUrl || "").trim();
    if (!url) return null;
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");

      if (host === "youtu.be") {
        const id = u.pathname.replace("/", "");
        return { kind: "youtube", src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
      }
      if (host === "youtube.com" || host === "m.youtube.com") {
        const id = u.searchParams.get("v");
        if (id) return { kind: "youtube", src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
      }
      if (host === "vimeo.com") {
        const id = u.pathname.split("/").filter(Boolean)[0];
        if (id) return { kind: "vimeo", src: `https://player.vimeo.com/video/${id}?autoplay=1` };
      }

      return { kind: "video", src: url };
    } catch {
      return { kind: "video", src: url };
    }
  }, [aboutVideo.videoUrl]);
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(companyInfo.whatsappMessage)}`,
      '_blank'
    );
  };

  const introParagraphs = Array.isArray(intro.paragraphs) ? intro.paragraphs : [];
  const introStats = Array.isArray(intro.stats) ? intro.stats : [];
  const pillarItems = Array.isArray(pillars.items) ? pillars.items : [];
  const timelineItems = Array.isArray(timeline.items) ? timeline.items : [];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 opacity-10">
          <img src={images.about} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute top-1/4 right-10 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-medium mb-6">
              {hero.badge || "Quem Somos"}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {hero.title || "Conheça a"} <span className="text-orange-500">{hero.highlight || "DRSUPORTI"}</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              {hero.description || "Somos uma empresa de Soluções em TI com foco em atender empresas que não podem parar. Nossa missão é garantir que sua tecnologia funcione de forma eficiente, segura e sem interrupções."}
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                {intro.title || "Tecnologia que faz sua empresa crescer"}
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                {introParagraphs.length > 0 ? (
                  introParagraphs.map((text, index) => (
                    <p key={index}>{text}</p>
                  ))
                ) : (
                  <p>
                    A DRSUPORTI nasceu da percepção de que muitas empresas enfrentam dificuldades
                    com sua infraestrutura de TI, seja por falta de pessoal qualificado,
                    problemas recorrentes ou dificuldade em acompanhar a evolução tecnológica.
                  </p>
                )}
              </div>

              <div className="mt-8 flex items-center gap-6">
                {introStats.map((stat, index) => {
                  const IconComponent = iconMap[stat?.icon];
                  return (
                    <div key={`${stat?.label || "stat"}-${index}`} className="flex items-center gap-3">
                      {IconComponent ? <IconComponent className="w-5 h-5 text-orange-500" /> : null}
                      <span className="text-slate-700 font-medium">{stat?.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 to-blue-500/20 rounded-3xl blur-2xl" />
              <img
                src={images.about}
                alt="Equipe DRSUPORTI"
                className="relative rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-orange-100 text-orange-600 text-sm font-medium mb-4">
              {videoSection.badge || "Vídeo Institucional"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              {videoSection.title || "Conheça a DRSUPORTI por dentro"}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {videoSection.description || "Veja como trabalhamos para manter sua empresa sempre operando"}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-900 aspect-video">
              {!isVideoPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <img
                    src={aboutVideo.coverUrl || images.services}
                    alt="Vídeo institucional DRSUPORTI"
                    className="w-full h-full object-cover opacity-50"
                  />
                  <button
                    onClick={() => {
                      if (videoEmbed) setIsVideoPlaying(true);
                    }}
                    className={`absolute w-20 h-20 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 group ${
                      videoEmbed
                        ? "bg-orange-500 hover:bg-orange-600 shadow-orange-500/30 hover:scale-110"
                        : "bg-slate-600 cursor-not-allowed opacity-70"
                    }`}
                  >
                    <Play className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform" />
                  </button>
                </div>
              )}
              {isVideoPlaying && (
                <div className="absolute inset-0 bg-black">
                  {videoEmbed?.kind === "video" ? (
                    <video
                      src={videoEmbed.src}
                      controls
                      autoPlay
                      playsInline
                      poster={aboutVideo.coverUrl || images.services}
                      className="w-full h-full"
                    />
                  ) : (
                    <iframe
                      title="Vídeo institucional"
                      src={videoEmbed?.src || ""}
                      className="w-full h-full"
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              {pillars.badge || "Nossos Pilares"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
              {pillars.title || "Missão, Visão e Valores"}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillarItems.map((item, index) => {
              const IconComponent = iconMap[item?.icon];
              return (
                <div
                  key={index}
                  className="text-center p-8 rounded-2xl bg-slate-50 hover:bg-orange-50 transition-colors duration-300"
                >
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6">
                    {IconComponent ? <IconComponent className="w-8 h-8 text-white" /> : null}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-orange-500/20 text-orange-400 text-sm font-medium mb-4">
              {timeline.badge || "Nossa História"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {timeline.title || "Uma trajetória de sucesso"}
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-orange-500 to-blue-500 rounded-full hidden md:block" />
            
            <div className="space-y-12">
              {timelineItems.map((item, index) => (
                <div
                  key={index}
                  className={`relative flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className={`p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 inline-block ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>
                      <span className="text-orange-500 font-bold text-lg">{item.year}</span>
                      <h3 className="text-xl font-bold text-white mt-1">{item.title}</h3>
                      <p className="text-slate-400 mt-2">{item.description}</p>
                    </div>
                  </div>
                  
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-orange-500 rounded-full border-4 border-slate-900 hidden md:block" />
                  
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-orange-100 text-orange-600 text-sm font-medium mb-4">
              {differentialsSection.badge || "Por que a DRSUPORTI"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
              {differentialsSection.title || "Nossos Diferenciais"}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {differentials.map((diff) => {
              const IconComponent = iconMap[diff.icon];
              return (
                <div
                  key={diff.id}
                  className="p-8 rounded-2xl border border-slate-200 hover:border-orange-200 hover:shadow-xl hover:shadow-orange-100 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6">
                    {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{diff.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{diff.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {cta.title || "Quer saber mais sobre a DRSUPORTI?"}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {cta.description || "Entre em contato e conheça nossas soluções personalizadas para sua empresa."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleWhatsApp}
              size="lg"
              className="bg-white text-orange-600 hover:bg-slate-100 font-semibold px-8 py-6 text-lg shadow-xl"
            >
              <Phone className="w-5 h-5 mr-2" />
              {cta.primaryLabel || "Fale com um especialista"}
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              <Link to={cta.secondaryHref || "/contato"}>{cta.secondaryLabel || "Ver formas de contato"}</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default QuemSomosPage;
